/**
 * AboutPanel — own-profile right-rail card showing the user's "About" rows.
 *
 * AboutRow.icon is a legacy SVG-markup string — NOT an IconName.
 * We use a fixed neutral <Icon name="check"> for every row instead.
 */
import styled from 'styled-components';
import { Panel, SectionLabel } from '@/ui';
import { Icon } from '@/icons';
import type { User } from '@/core/models';

/* ── Styled helpers ─────────────────────────────────────────────────────────── */

const RowList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.4;
`;

const IconWrap = styled.span`
  flex-shrink: 0;
  margin-top: 2px;
  color: ${({ theme }) => theme.color.textMuted};
`;

const EmptyState = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Component ──────────────────────────────────────────────────────────────── */

export interface AboutPanelProps {
  user: User;
}

export function AboutPanel({ user }: AboutPanelProps) {
  const rows = user.about ?? [];

  return (
    <Panel>
      <SectionLabel as="h2">About</SectionLabel>

      {rows.length === 0 ? (
        <EmptyState>No bio details yet.</EmptyState>
      ) : (
        <RowList>
          {rows.map((row, i) => (
            <Row key={i}>
              <IconWrap>
                {/* row.icon is a legacy SVG string — always use a fixed neutral icon */}
                <Icon name="check" size={14} />
              </IconWrap>
              {row.text}
            </Row>
          ))}
        </RowList>
      )}
    </Panel>
  );
}
